import { useMutation, useQueryClient } from "@tanstack/react-query";

import { TOOL_REGISTRY_KEY } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface UpdateToolLabelProtectionParams {
  labelId: number;
  is_protected: boolean;
  approver_role_id: number | null;
}

interface UpdateToolLabelProtectionResponse {
  success: boolean;
}

export const useUpdateToolLabelProtection = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      labelId,
      is_protected,
      approver_role_id,
    }: UpdateToolLabelProtectionParams): Promise<UpdateToolLabelProtectionResponse> => {
      const res = await fetch(`${ENDPOINTS.tool_registry.label(labelId)}/protection`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ is_protected, approver_role_id }),
      });
      return handleResponse(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TOOL_REGISTRY_KEY] });
      displayToast("Label configuration updated successfully", ToastType.success);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      displayErrorToast(message || "Failed to update label protection");
    },
  });
};
